import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useUserRequests } from "@/features/user-requests/hooks/use-user-requests";
import { UserDetailsDrawer } from "./user-details-drawer";
import ProfileUpdatesList from "@/features/user-requests/ui/profile-updates-list";
import ProfessionalChangeList from "@/features/user-requests/ui/professional-change-list";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

const PendingRequestsList = () => {
  // Counts come from the new UserRequest source for both types. Limit 1 since
  // we only consume the paginator's `total` for the badge.
  const { data: profChange } = useUserRequests({
    type: "PROFESSIONAL_CHANGE",
    status: "OPEN",
    page: 0,
    limit: 1,
  });
  const { data: profileUpdates } = useUserRequests({
    type: "PROFILE_UPDATE",
    status: "OPEN",
    page: 0,
    limit: 1,
  });

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "professional-change";

  // The two tabs have different status option sets, so switching tabs clears
  // the filter params (status/sort/date/search/page) rather than carrying over
  // a value that's invalid for the other tab.
  const handleTabChange = (tab: string) => {
    setSearchParams(new URLSearchParams({ tab }));
  };

  const handleUserClick = (userId: string) => {
    setSelectedUserId(userId);
    setIsDrawerOpen(true);
  };

  const profCount = profChange?.total ?? 0;
  const profileCount = profileUpdates?.total ?? 0;

  return (
    <>
      <div className="flex flex-col gap-4 p-4">
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <TabsList>
            <TabsTrigger value="professional-change">
              Professional Change
              {profCount > 0 && (
                <Badge className="ml-2 bg-red-500 text-white text-xs">
                  {profCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="profile-updates">
              Profile Updates
              {profileCount > 0 && (
                <Badge className="ml-2 bg-red-500 text-white text-xs">
                  {profileCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="professional-change">
            <ProfessionalChangeList onUserClick={handleUserClick} />
          </TabsContent>

          <TabsContent value="profile-updates">
            <ProfileUpdatesList onUserClick={handleUserClick} />
          </TabsContent>
        </Tabs>
      </div>

      <UserDetailsDrawer
        userId={selectedUserId}
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
      />
    </>
  );
};

export default PendingRequestsList;
